// covered-by: frontends/carrie/src/ask-v2/project/__tests__/AskV2OpenItems.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Check } from "@phosphor-icons/react";
import { Button, Popover, Text, TextField } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2OpenItemAnswer } from "./AskV2OpenItemAnswer";
import {
  findingQuote,
  findingSuggestion,
  openItemSeverity,
} from "./openItemFindingFields";
import { openItemKindLabel } from "./openItemKindLabel";
import {
  ASK_V2_PROJECT_OWNER_NAME_FALLBACK,
  openItemGraySubtitle,
  openItemRowTitle,
  openItemShortNeeded,
} from "./openItemRowTitle";
import { listOpenItemAssignees } from "./openItemsApi";
import {
  threadQuestionForOpenItem,
  type OpenItemThreadQuestion,
} from "./openItemThreadQuestions";
import { formatOpenItemAge } from "./useProjectOpenItems";

type Member = {
  _id: string;
  name?: string | null;
  email?: string | null;
};

type AssigneeRow = { userId: string; name: string };

// why: a pending nudge and its badge are a fact about the owner who was
// pinged. Keying by row id alone left the badge on whoever the row showed
// next, including an owner the ping never reached.
export function nudgeKey(itemId: string, ownerUserId: string): string {
  return JSON.stringify([itemId, ownerUserId]);
}

const TITLE_CLASS =
  "text-[15px] font-medium leading-snug text-puntt-neutral-gray-12";

function assignedSuggestions(item: AskPunttOpenItem): string[] {
  if (item.kind !== "source_conflict") return [];
  if (!item.ownerUserId) return [];
  return (item.suggestions ?? []).map((label) => label.trim()).filter(Boolean);
}

function OpenItemSuggestions({ item }: { item: AskPunttOpenItem }) {
  const suggestions = assignedSuggestions(item);
  if (suggestions.length === 0) return null;
  return (
    <span className="mt-1 block">
      {suggestions.map((label, suggestionIndex) => (
        <span
          key={`${item.id}-${suggestionIndex}`}
          className="block text-[12px] leading-snug text-puntt-neutral-gray-11"
          data-testid={`ask-v2-open-item-suggestion-${item.id}-${suggestionIndex}`}
        >
          {label}
        </span>
      ))}
    </span>
  );
}

export function ownerLabel(item: AskPunttOpenItem, members: Member[]): string {
  const member = members.find((row) => row._id === item.ownerUserId);
  const fromName = member?.name?.trim() ?? "";
  const fromEmail = member?.email?.trim() ?? "";
  if (fromName.length > 0) return fromName;
  if (fromEmail.length > 0) return fromEmail;
  return item.ownerUserId ?? "";
}

function MetaDot() {
  return (
    <span aria-hidden="true" className="text-puntt-neutral-gray-8">
      ·
    </span>
  );
}

function AssignOpenItemPopover({
  projectId,
  item,
  ownerDisplayName,
  emphasized,
  open,
  pending,
  onOpenChange,
  onAssign,
}: {
  projectId: string;
  item: AskPunttOpenItem;
  ownerDisplayName: string;
  emphasized: boolean;
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onAssign: (ownerUserId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const shortNeeded = openItemShortNeeded(item);
  const triggerLabel =
    ownerDisplayName.length > 0
      ? ownerDisplayName
      : t("askV2.openItems.assign");
  const triggerAriaLabel =
    ownerDisplayName.length > 0
      ? t("askV2.openItems.changeOwnerAria", {
          needed: shortNeeded,
          owner: ownerDisplayName,
        })
      : t("askV2.openItems.assignAria", { needed: shortNeeded });
  const [search, setSearch] = useState("");
  const assigneesQuery = useQuery({
    queryKey: ["ask-puntt-open-item-assignees", projectId],
    queryFn: () => listOpenItemAssignees(projectId),
    enabled: open && projectId.length > 0,
  });
  const assignees = assigneesQuery.data?.assignees ?? [];
  // why: `data` is undefined until the roster lands, so an unguarded empty list
  // told every opener "there is nobody to assign on this project" while the
  // fetch was still in flight. `isLoading` is `isPending && isFetching`, so a
  // disabled query (closed popover, blank project) never sticks on loading.
  const assigneesLoading = assigneesQuery.isLoading;
  const needle = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      needle.length === 0
        ? assignees
        : assignees.filter((row) => row.name.toLowerCase().includes(needle)),
    [assignees, needle],
  );
  const triggerClass = emphasized
    ? `!h-auto !justify-start !p-0 !font-medium leading-snug hover:bg-transparent ${TITLE_CLASS}`
    : "!h-auto !justify-start !p-0 text-[12px] !font-normal leading-snug text-puntt-neutral-gray-10 underline-offset-2 hover:bg-transparent hover:underline";

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearch("");
        onOpenChange(next);
      }}
    >
      <Popover.Trigger>
        <Button
          type="button"
          variant="ghost"
          size="1"
          data-testid={`ask-v2-open-item-assign-${item.id}`}
          aria-label={triggerAriaLabel}
          disabled={pending}
          className={triggerClass}
        >
          {triggerLabel}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="start"
        className="max-h-[min(70vh,420px)] w-[280px] overflow-y-auto p-2"
        data-testid={`ask-v2-open-item-assign-popover-${item.id}`}
      >
        <TextField.Root
          size="2"
          className="mb-2"
          data-testid={`ask-v2-open-item-assign-search-${item.id}`}
          placeholder={t("askV2.openItems.assignSearchPlaceholder")}
          aria-label={t("askV2.openItems.assignSearchPlaceholder")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {assigneesLoading ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-loading">
            {t("loading")}
          </Text>
        ) : assignees.length === 0 ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-empty">
            {t("askV2.openItems.assignEmpty")}
          </Text>
        ) : filtered.length === 0 ? (
          <Text size="2" data-testid="ask-v2-open-item-assign-no-match">
            {t("askV2.openItems.assignNoMatch")}
          </Text>
        ) : (
          <ul className="flex max-h-[240px] flex-col gap-0.5 overflow-y-auto">
            {filtered.map((row: AssigneeRow) => {
              const isOwner = item.ownerUserId === row.userId;
              return (
                <li key={row.userId} className="list-none">
                  <button
                    type="button"
                    data-testid={`ask-v2-open-item-expert-${row.userId}`}
                    aria-current={isOwner ? "true" : undefined}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-puntt-neutral-gray-3"
                    onClick={() => {
                      onAssign(row.userId);
                      setSearch("");
                      onOpenChange(false);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                    {isOwner ? (
                      <Check
                        size={14}
                        className="text-puntt-brand-purple-9 shrink-0"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function OpenItemTitle({
  item,
  owner,
  selected,
  hideSuggestions,
  onAttach,
}: {
  item: AskPunttOpenItem;
  owner: string;
  selected: boolean;
  hideSuggestions: boolean;
  onAttach: (item: AskPunttOpenItem) => void;
}) {
  const { t } = useTranslation("ui");
  const title = openItemRowTitle(item);
  const subtitle = openItemGraySubtitle(item, openItemKindLabel(item.kind, t));
  const severity = openItemSeverity(item);
  const quote = findingQuote(item);
  const fix = findingSuggestion(item);
  const suggestions = hideSuggestions ? [] : assignedSuggestions(item);
  const suggestionLabel =
    suggestions.length > 0 ? `, ${suggestions.join(", ")}` : "";
  const fixLabel = fix.length > 0 ? `. ${fix}` : "";
  const quoteLabel = quote.length > 0 ? `. ${quote}` : "";
  const severityLabel = severity.length > 0 ? `${severity}. ` : "";
  const ariaQuestion = subtitle.length > 0 ? `${title}. ${subtitle}` : title;
  return (
    <button
      type="button"
      data-testid={`ask-v2-open-item-${item.id}`}
      aria-current={selected ? "true" : undefined}
      aria-label={`${severityLabel}${ariaQuestion}${quoteLabel}${fixLabel}${suggestionLabel}, ${owner}`}
      onClick={() => onAttach(item)}
      className="focus-visible:outline-puntt-brand-purple-9 flex w-full items-start gap-2 rounded-md bg-transparent text-left text-[15px] text-puntt-neutral-gray-12 hover:bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{ backgroundColor: "transparent" }}
    >
      <span className="min-w-0">
        {severity.length > 0 ? (
          <span
            className="mb-1 block text-[12px] font-semibold capitalize leading-snug text-puntt-neutral-gray-12"
            data-testid={`ask-v2-open-item-severity-${item.id}`}
          >
            {severity}
          </span>
        ) : null}
        <span
          className={`block ${TITLE_CLASS}`}
          data-testid={`ask-v2-open-item-title-${item.id}`}
        >
          {title}
        </span>
        {/* why: the subtitle is the cleaned remainder or a recorded title, never `item.needed` — that payload still carries `**Issue:**` / `**Fix:**` markers. */}
        {subtitle.length > 0 ? (
          <span
            className="mt-1 block text-[13px] leading-snug text-puntt-neutral-gray-10"
            data-testid={`ask-v2-open-item-subtitle-${item.id}`}
          >
            {subtitle}
          </span>
        ) : null}
        {quote.length > 0 ? (
          <span
            className="mt-1 block border-l-2 border-puntt-neutral-gray-6 pl-2 text-[13px] leading-snug text-puntt-neutral-gray-10"
            data-testid={`ask-v2-open-item-quote-${item.id}`}
          >
            {quote}
          </span>
        ) : null}
        {fix.length > 0 ? (
          // why: a finding's fix and a conflict's suggestion list never share a row — kinds are exclusive — so this literal index cannot collide with OpenItemSuggestions.
          <span
            className="mt-1 block text-[13px] leading-snug text-puntt-neutral-gray-12"
            data-testid={`ask-v2-open-item-suggestion-${item.id}-0`}
          >
            {fix}
          </span>
        ) : null}
        {hideSuggestions ? null : <OpenItemSuggestions item={item} />}
      </span>
    </button>
  );
}

export function OpenItemRows({
  projectId,
  items,
  members,
  mayAssign,
  onAssign,
  assignPending,
  onHandToOwner,
  handedItemIds,
  projectOwnerId,
  projectOwnerName,
  currentUserId,
  onNudge,
  pendingNudges,
  nudgedOwnerByItemId,
  onAttach,
  selectedItemId,
  threadQuestions,
  answersDisabled = false,
  onSend,
}: {
  projectId: string;
  items: AskPunttOpenItem[];
  members: Member[];
  mayAssign: boolean;
  onAssign: (itemId: string, ownerUserId: string) => void;
  assignPending: boolean;
  onHandToOwner: (itemId: string) => void;
  handedItemIds: ReadonlySet<string>;
  projectOwnerId: string;
  projectOwnerName: string;
  currentUserId: string | undefined;
  onNudge: (itemId: string, ownerUserId: string) => void;
  pendingNudges: ReadonlySet<string>;
  nudgedOwnerByItemId: ReadonlyMap<string, string>;
  onAttach: (item: AskPunttOpenItem) => void;
  selectedItemId: string | null;
  threadQuestions: readonly OpenItemThreadQuestion[];
  answersDisabled?: boolean;
  onSend: (message: string) => void;
}) {
  const { t } = useTranslation("ui");
  const [assignOpenItemId, setAssignOpenItemId] = useState<string | null>(null);
  if (items.length === 0) return null;
  const viewerIsOwner =
    projectOwnerId.length > 0 && currentUserId === projectOwnerId;
  return (
    <>
      {items.map((item) => {
        const owner = ownerLabel(item, members);
        const ownerUserId = item.ownerUserId ?? "";
        const nudged =
          ownerUserId.length > 0 &&
          nudgedOwnerByItemId.get(item.id) === ownerUserId;
        const age = formatOpenItemAge(item.openedAt, new Date());
        const handedOff = handedItemIds.has(item.id);
        const serverHasOwner =
          projectOwnerId.length > 0 && item.ownerUserId === projectOwnerId;
        const ownerNameIsPlaceholder =
          projectOwnerName === ASK_V2_PROJECT_OWNER_NAME_FALLBACK ||
          projectOwnerName.trim().length === 0;
        const handedOwnerLabel = ownerNameIsPlaceholder
          ? t("askV2.openItems.projectOwnerFallback")
          : projectOwnerName;
        const showHandedLine = handedOff && !serverHasOwner;
        const triggerName = handedOff ? handedOwnerLabel : owner;
        const handedLabel = ownerNameIsPlaceholder
          ? t("askV2.openItems.handedToProjectOwner")
          : t("askV2.openItems.handedTo", { name: projectOwnerName });
        const selected = selectedItemId === item.id;
        const question = threadQuestionForOpenItem(item, threadQuestions);
        return (
          <li
            key={item.id}
            data-testid={`ask-v2-open-item-row-${item.id}`}
            className={`flex list-none flex-col gap-1.5 rounded-lg p-3 ${
              selected ? "bg-puntt-neutral-gray-3" : ""
            }`}
          >
            <OpenItemTitle
              item={item}
              owner={owner}
              selected={selected}
              hideSuggestions={question != null}
              onAttach={onAttach}
            />
            <AskV2OpenItemAnswer
              item={item}
              question={question}
              disabled={answersDisabled}
              onAttach={onAttach}
              onSend={onSend}
            />
            {/* why: the title is a button — age and assign live beside it so a click cannot both attach and reassign */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-snug text-puntt-neutral-gray-10">
              <span data-testid={`ask-v2-open-item-age-${item.id}`}>{age}</span>
              {item.fileName ? (
                <>
                  <MetaDot />
                  <span data-testid={`ask-v2-open-item-file-${item.id}`}>
                    {item.fileName}
                  </span>
                </>
              ) : null}
              <MetaDot />
              <AssignOpenItemPopover
                projectId={projectId}
                item={item}
                ownerDisplayName={triggerName}
                emphasized={handedOff}
                open={assignOpenItemId === item.id}
                pending={assignPending}
                onOpenChange={(next) => {
                  setAssignOpenItemId(next ? item.id : null);
                }}
                onAssign={(ownerUserId) => {
                  onAssign(item.id, ownerUserId);
                }}
              />
              {showHandedLine ? (
                <>
                  <MetaDot />
                  <span
                    data-testid={`ask-v2-open-item-handed-${item.id}`}
                    aria-label={handedLabel}
                  >
                    {handedLabel}
                  </span>
                </>
              ) : null}
              {mayAssign ? (
                <>
                  <MetaDot />
                  {nudged ? (
                    <span
                      data-testid={`ask-v2-open-item-nudged-${item.id}`}
                      aria-label={t("askV2.openItems.nudged")}
                      className="shrink-0 text-[12px] leading-snug text-puntt-neutral-gray-10"
                    >
                      {t("askV2.openItems.nudged")}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      data-testid={`ask-v2-open-item-nudge-${item.id}`}
                      aria-label={`Nudge ${owner} about ${openItemShortNeeded(item)}`}
                      disabled={
                        ownerUserId.length === 0 ||
                        pendingNudges.has(nudgeKey(item.id, ownerUserId))
                      }
                      className="!h-auto shrink-0 !justify-start !p-0 text-[12px] !font-normal leading-snug text-puntt-neutral-gray-10 underline-offset-2 hover:bg-transparent hover:underline"
                      onClick={() => {
                        onNudge(item.id, ownerUserId);
                      }}
                    >
                      {t("askV2.openItems.nudge")}
                    </Button>
                  )}
                </>
              ) : null}
            </div>
            {/* why: handing the item back to the project owner is a row action — the assign menu is only the roster */}
            {projectOwnerId.length > 0 && viewerIsOwner === false ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid={`ask-v2-open-item-skip-${item.id}`}
                  aria-label={t("askV2.openItems.skipAria", {
                    needed: openItemShortNeeded(item),
                  })}
                  className="cursor-pointer rounded-md px-2 py-1 text-[13px] font-medium text-puntt-neutral-gray-12 hover:bg-puntt-neutral-gray-3"
                  onClick={() => {
                    onHandToOwner(item.id);
                  }}
                >
                  {t("askV2.openItems.skip")}
                </button>
              </div>
            ) : null}
          </li>
        );
      })}
    </>
  );
}
